import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import usePrefersReducedMotion from '../../utils/usePrefersReducedMotion';
import './shrinkable.css';

/* Every box that opens in Buffy's chat can fold to a small tile (Sandy
   2026-10-16: "every box that pops up needs an ability to shrink it into a
   little like 2 by 1 box"; then "show the actual graphic, just shrunken").
   The tile is a picture of the box: a still copy of its white card, scaled to
   fit and cropped on a whole row, inside the box's own lilac frame. The box
   itself scales down into the tile (and the tile back up into the box) while
   the space around eases, so the fold reads as one thing shrinking. The box
   keeps its state while folded (hidden, not unmounted). Boxes put
   <ShrinkButton/> where their controls are; it draws nothing outside a
   <Shrinkable>. */

const ShrinkContext = createContext(null);
const EASE = 'cubic-bezier(.22,.8,.26,1)';
const MS = 320;
const TILE_W = 240;
const TILE_H = 120;
const FRAME = 6; // the lilac frame around the picture
// In the picture: words and controls go, the shape stays (Sandy: "the actual
// graphic"). Rows below the window are dropped so the crop ends on a row line.
const DROP = '.shk-btn, .bc-foot, .bi-fr-read, .bi-fr-miss, .psb-examples, .psb-fix, .psb-add, .dch, .bc-problem, .asb-students';
const ROWS = '.bi-fr-row, .psb-row, .asb-list > li, .sp-list > li';

// A still, inert copy for the tile: no ids, no focus, no labels, nothing a
// screen reader reads twice; text fields keep what is typed in them now.
function snapshot(node) {
  const copy = node.cloneNode(true);
  copy.querySelectorAll(DROP).forEach(el => el.remove());
  const live = node.querySelectorAll('textarea, input');
  copy.querySelectorAll('textarea, input').forEach((el, i) => { if (el.tagName === 'TEXTAREA') el.textContent = live[i]?.value ?? el.textContent; else el.setAttribute('value', live[i]?.value ?? ''); });
  [copy, ...copy.querySelectorAll('[id]')].forEach(el => el.removeAttribute('id'));
  copy.querySelectorAll('button, a, input, select, textarea, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
  copy.querySelectorAll('[aria-label], [aria-labelledby], [role], label[for]').forEach(el => { el.removeAttribute('aria-label'); el.removeAttribute('aria-labelledby'); el.removeAttribute('role'); el.removeAttribute('for'); });
  return copy;
}

// phase: open -> folding -> shrunk -> unfolding -> open. While folding or
// unfolding both are drawn: the tile sits still under the box at its own
// size while the real box scales between its size and the tile's and fades
// (2026-10-16: scaling the tile itself blew its lilac frame up into a thick
// band). Only the box ever scales; the tile's frame stays crisp.
export function Shrinkable({ title, label, children }) {
  const [phase, setPhase] = useState('open');
  const tile = useRef(null);
  const thumb = useRef(null);
  const box = useRef(null);
  const wrap = useRef(null);
  const inner = useRef(null);
  const fromHeight = useRef(0);
  const picture = useRef(null); // { node, cardWidth, boxWidth, marginTop }
  const placed = useRef(null);
  const moved = useRef(false);
  const reduced = usePrefersReducedMotion();
  const withTile = phase !== 'open';
  const moving = phase === 'folding' || phase === 'unfolding';

  useLayoutEffect(() => {
    // The picture: scaled to the window, cropped on the last whole row.
    if (withTile && thumb.current && picture.current && placed.current !== picture.current) {
      const { node, cardWidth } = picture.current;
      const scale = (TILE_W - FRAME * 2) / cardWidth;
      thumb.current.replaceChildren(node);
      node.style.width = `${cardWidth}px`;
      node.style.transform = `scale(${scale})`;
      const windowH = (TILE_H - FRAME * 2) / scale;
      const top = node.getBoundingClientRect().top;
      node.querySelectorAll(ROWS).forEach(row => { if ((row.getBoundingClientRect().bottom - top) / scale > windowH) row.style.display = 'none'; });
      placed.current = picture.current;
    }
    if (!moving) return undefined;
    const el = wrap.current;
    const w = picture.current?.boxWidth || TILE_W;
    const small = `scale(${TILE_W / w})`;
    const opts = { duration: MS, easing: EASE, fill: 'both' };
    const tileOuter = TILE_H + (parseFloat(picture.current?.marginTop) || 0) + 4;
    const to = phase === 'folding' ? tileOuter : inner.current.offsetHeight;
    el.style.overflow = 'clip';
    const space = el.animate([{ height: `${fromHeight.current}px` }, { height: `${to}px` }], opts);
    // The two barely overlap, so their text never shows doubled: folding, the
    // box is gone by 55% and the tile comes in from 50%; unfolding, the tile is
    // gone by 30% and the box shows from 25%.
    const boxMove = box.current.animate(phase === 'folding'
      ? [{ transform: 'none', opacity: 1 }, { opacity: 0, offset: 0.55 }, { transform: small, opacity: 0 }]
      : [{ transform: small, opacity: 0 }, { opacity: 0, offset: 0.25 }, { transform: 'none', opacity: 1 }], opts);
    const tileFade = tile.current?.animate(phase === 'folding'
      ? [{ opacity: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 1 }]
      : [{ opacity: 1 }, { opacity: 0, offset: 0.3 }, { opacity: 0 }], opts);
    let alive = true;
    boxMove.onfinish = () => { if (alive) setPhase(phase === 'folding' ? 'shrunk' : 'open'); };
    return () => {
      alive = false;
      [space, boxMove, tileFade].forEach(a => a?.cancel());
      el.style.overflow = '';
    };
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  // Focus follows the fold: onto the tile, then back into the opened box.
  // preventScroll: focusing must not slide the chat.
  useEffect(() => {
    if (!moved.current || moving) return;
    moved.current = false;
    if (phase === 'shrunk') tile.current?.focus({ preventScroll: true });
    else box.current?.querySelector('.shk-btn')?.focus({ preventScroll: true });
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const animates = () => !reduced && typeof wrap.current?.animate === 'function';
  const fold = to => {
    if (moving) return;
    fromHeight.current = wrap.current?.offsetHeight || 0;
    const node = box.current?.firstElementChild;
    if (to && node) {
      // The whole box is copied (its classes carry its layout); the tile hides
      // the copy's own wallpaper and padding, so only the card shows.
      const card = node.querySelector('.bc-card') || node;
      picture.current = { node: snapshot(node), cardWidth: card.getBoundingClientRect().width || TILE_W,
        boxWidth: node.getBoundingClientRect().width || TILE_W, marginTop: getComputedStyle(node).marginTop };
    }
    moved.current = true;
    setPhase(to ? (animates() ? 'folding' : 'shrunk') : (animates() ? 'unfolding' : 'open'));
  };
  const marginTop = picture.current?.marginTop || '0px';
  return <ShrinkContext.Provider value={() => fold(true)}>
    <div ref={wrap} className="shk-wrap"><div ref={inner} className={`shk-inner${moving ? ' is-moving' : ''}`}>
      {withTile && <button ref={tile} type="button" className="shk-tile" style={{ width: TILE_W, height: TILE_H, ...(moving ? { top: `calc(${marginTop} + 2px)` } : { marginTop }) }}
        aria-label={`Open ${title}${label ? `, ${label}` : ''}`} title="Open it again" tabIndex={moving ? -1 : undefined} onClick={() => fold(false)}>
        <span ref={thumb} className="shk-thumb" aria-hidden="true" inert="" />
        <span className="shk-badge" aria-hidden="true"><Maximize2 size={12} /></span>
      </button>}
      {/* Hidden, not unmounted: what the teacher typed or opened stays. */}
      <div ref={box} hidden={phase === 'shrunk'} className="shk-box">{children}</div>
    </div></div>
  </ShrinkContext.Provider>;
}

export function ShrinkButton({ className = '' }) {
  const shrink = useContext(ShrinkContext);
  if (!shrink) return null;
  return <button type="button" className={`shk-btn ${className}`} aria-label="Shrink this box" title="Shrink" onClick={shrink}><Minimize2 size={14} aria-hidden="true" /></button>;
}
